/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { CartLine, ProductDeliveryOptions } from '@msdyn365-commerce/retail-proxy';

/**
 * Delivery Mode Charge Configuration from app.settings.json
 */
export interface IDeliveryModeCharge {
    /** Delivery mode code (e.g., 'HD', 'STD-2', 'STD') */
    code: string;
    /** Charge amount for this delivery mode */
    charge: number;
}

/**
 * Configuration for delivery charges from app.settings.json
 */
export interface IDeliveryChargesConfig {
    /** Array of delivery mode charges */
    deliveryModeCharges: IDeliveryModeCharge[];
    /** Modes to ignore when calculating delivery (e.g., 'Clickcol', 'Common MOD') */
    ignoredModes: string[];
    /** Free delivery threshold amount */
    freeDeliveryMilestone: number;
    /** Standard delivery charge (fallback) */
    chargesForHomeDeliveryItems: number;
    /** Whether to apply HD charges */
    addChargesForHDItems: boolean;
}

/**
 * Result of delivery charge calculation
 */
export interface IDeliveryChargeResult {
    /** The calculated charge amount */
    chargeAmount: number;
    /** The delivery message to display */
    deliveryMessage: string | null;
    /** Whether free delivery threshold applies */
    isFreeDelivery: boolean;
    /** The dominant delivery mode in the cart */
    dominantMode: string | null;
    /** Whether the cart has mixed delivery methods */
    isMixedDelivery: boolean;
    /** Amount needed to reach free delivery (null if not applicable) */
    amountForFreeDelivery: number | null;
}

/**
 * Cart analysis result
 */
export interface ICartAnalysis {
    /** Whether the cart is CNC only */
    isCncOnlyCart: boolean;
    /** Home delivery items count */
    homeDeliveryItemsCount: number;
    /** CNC items count */
    cncItemsCount: number;
    /** Total quantity */
    totalQuantity: number;
    /** Delivery modes in cart (excluding ignored modes) */
    relevantDeliveryModes: string[];
    /** Whether the cart has mixed delivery methods */
    isMixedDelivery: boolean;
    /** The delivery mode with highest charge */
    dominantMode: string | null;
    /** The highest charge amount */
    dominantCharge: number;
}

// Known delivery mode categories for display messages
const XL_MODES = ['HD', 'HD Presale'];
const LARGE_MODES = ['STD-2'];
const STANDARD_MODES = ['STD', 'PF'];

/**
 * Utility class for delivery charges calculation
 * This class provides configuration-driven delivery charge logic
 * that can be reused across PDP, Cart, and Checkout
 */
export class DeliveryChargesUtil {
    private config: IDeliveryChargesConfig;
    private chargeMap: Map<string, number>;

    constructor(actionContext: IActionContext) {
        this.config = this.loadConfig(actionContext);
        this.chargeMap = this.buildChargeMap();
    }

    /**
     * Load configuration from app.settings.json
     */
    private loadConfig(actionContext: IActionContext): IDeliveryChargesConfig {
        const appConfig = actionContext.requestContext.app.config;

        // Load delivery mode charges from config or build defaults from legacy settings
        const deliveryModeCharges: IDeliveryModeCharge[] = appConfig.deliveryModeCharges || this.getDefaultCharges(appConfig);

        return {
            deliveryModeCharges,
            ignoredModes: appConfig.ignoredDeliveryModes || ['Clickcol', 'Common MOD'],
            freeDeliveryMilestone: appConfig.freeDeliveryMilestone || 50,
            chargesForHomeDeliveryItems: appConfig.chargesForHomeDeliveryItems || 5.95,
            addChargesForHDItems: appConfig.addChargesForHDItems ?? false
        };
    }

    /**
     * Get default charges for backward compatibility with legacy config
     */
    private getDefaultCharges(appConfig: any): IDeliveryModeCharge[] {
        const hdCharge = appConfig.chargesForHDItems || 15;
        const std2Charge = appConfig.chargesForSTD2Items || 15;
        const stdCharge = appConfig.chargesForHomeDeliveryItems || 5.95;

        return [
            { code: 'HD', charge: hdCharge },
            { code: 'HD Presale', charge: hdCharge },
            { code: 'STD-2', charge: std2Charge },
            { code: 'STD', charge: stdCharge },
            { code: 'PF', charge: stdCharge }
        ];
    }

    /**
     * Build a map of delivery mode codes to their charges for fast lookup
     */
    private buildChargeMap(): Map<string, number> {
        const map = new Map<string, number>();
        for (const item of this.config.deliveryModeCharges) {
            map.set(item.code.toLowerCase(), item.charge);
        }
        return map;
    }

    /**
     * Get charge for a delivery mode code
     */
    public getChargeForMode(modeCode: string): number {
        return this.chargeMap.get(modeCode.toLowerCase()) ?? this.config.chargesForHomeDeliveryItems;
    }

    /**
     * Check if a mode should be ignored
     */
    public isIgnoredMode(modeCode: string): boolean {
        return this.config.ignoredModes.some(ignored => ignored.toLowerCase() === modeCode.toLowerCase());
    }

    /**
     * Check if mode is XL delivery
     */
    public isXLMode(modeCode: string): boolean {
        return XL_MODES.some(m => m.toLowerCase() === modeCode.toLowerCase());
    }

    /**
     * Check if mode is Large delivery
     */
    public isLargeMode(modeCode: string): boolean {
        return LARGE_MODES.some(m => m.toLowerCase() === modeCode.toLowerCase());
    }

    /**
     * Check if mode is Standard delivery
     */
    public isStandardMode(modeCode: string): boolean {
        return STANDARD_MODES.some(m => m.toLowerCase() === modeCode.toLowerCase());
    }

    /**
     * Analyze cart to determine delivery modes and charges
     */
    public analyzeCart(cartState: ICartState, pickupDeliveryModeCode?: string): ICartAnalysis {
        const cartLines = cartState.cart.CartLines || [];

        // Separate home delivery and CNC items
        const homeDeliveryItems = cartLines.filter(line => !line.FulfillmentStoreId || line.FulfillmentStoreId === '');
        const cncItems = cartLines.filter(line => line.FulfillmentStoreId && line.FulfillmentStoreId !== '');

        // Calculate quantities
        const homeDeliveryItemsCount = homeDeliveryItems.reduce((sum, item) => sum + (item.Quantity || 0), 0);
        const cncItemsCount = cncItems.reduce((sum, item) => sum + (item.Quantity || 0), 0);

        // Get relevant delivery modes (excluding ignored modes)
        const deliveryModes = cartLines
            .map(line => line.DeliveryMode?.trim())
            .filter((mode): mode is string => Boolean(mode) && !this.isIgnoredMode(mode || ''));

        // Find the mode with highest charge
        let dominantMode: string | null = null;
        let dominantCharge = 0;

        for (const mode of deliveryModes) {
            const charge = this.getChargeForMode(mode);
            if (charge > dominantCharge) {
                dominantCharge = charge;
                dominantMode = mode;
            }
        }

        // Check for mixed delivery
        const uniqueModes = [...new Set(deliveryModes.map(m => m.toLowerCase()))];
        const isOnlyStandardModes = uniqueModes.every(mode => this.isStandardMode(mode));
        const isMixedDelivery = uniqueModes.length > 1 && !isOnlyStandardModes;

        return {
            isCncOnlyCart: homeDeliveryItems.length === 0 && cncItems.length > 0,
            homeDeliveryItemsCount,
            cncItemsCount,
            totalQuantity: homeDeliveryItemsCount + cncItemsCount,
            relevantDeliveryModes: deliveryModes,
            isMixedDelivery,
            dominantMode,
            dominantCharge
        };
    }

    /**
     * Analyze delivery options to find the highest charge mode
     */
    public analyzeDeliveryOptions(
        deliveryOptions: ProductDeliveryOptions[],
        cartLines: CartLine[]
    ): { dominantMode: string | null; dominantCharge: number; hasXLItem: boolean; hasLargeItem: boolean } {
        let dominantMode: string | null = null;
        let dominantCharge = 0;
        let hasXLItem = false;
        let hasLargeItem = false;

        for (const productOption of deliveryOptions) {
            // Check if this product is a CNC item (skip for CNC items)
            const isCncItem = cartLines.find(
                cl => cl.ProductId === productOption.ProductId && cl.FulfillmentStoreId && cl.FulfillmentStoreId !== ''
            );

            if (isCncItem) continue;

            for (const option of productOption.DeliveryOptions || []) {
                const code = option.Code;
                if (!code) continue;

                if (this.isXLMode(code)) hasXLItem = true;
                if (this.isLargeMode(code)) hasLargeItem = true;

                const charge = this.getChargeForMode(code);
                if (charge > dominantCharge) {
                    dominantCharge = charge;
                    dominantMode = code;
                }
            }
        }

        return { dominantMode, dominantCharge, hasXLItem, hasLargeItem };
    }

    /**
     * Calculate delivery charges for the cart
     */
    public calculateDeliveryCharges(
        cartState: ICartState,
        deliveryOptions: ProductDeliveryOptions[],
        pickupDeliveryModeCode?: string
    ): IDeliveryChargeResult {
        const cartAnalysis = this.analyzeCart(cartState, pickupDeliveryModeCode);
        const optionsAnalysis = this.analyzeDeliveryOptions(deliveryOptions, cartState.cart.CartLines || []);
        const subTotalAmount = cartState.cart.SubtotalAmount || 0;

        // CNC only cart - no charges
        if (cartAnalysis.isCncOnlyCart) {
            return {
                chargeAmount: 0,
                deliveryMessage: null,
                isFreeDelivery: true,
                dominantMode: null,
                isMixedDelivery: false,
                amountForFreeDelivery: null
            };
        }

        const dominantMode = optionsAnalysis.dominantMode;
        const dominantCharge = optionsAnalysis.dominantCharge;

        // If no delivery options found, return no charges
        if (!dominantMode) {
            return {
                chargeAmount: 0,
                deliveryMessage: null,
                isFreeDelivery: true,
                dominantMode: null,
                isMixedDelivery: false,
                amountForFreeDelivery: null
            };
        }

        // Determine charge amount
        let chargeAmount = dominantCharge;
        let isFreeDelivery = false;
        let amountForFreeDelivery: number | null = null;

        // Check if XL mode and whether to apply charges
        const isXL = this.isXLMode(dominantMode);
        const isLarge = this.isLargeMode(dominantMode);
        const isStandard = this.isStandardMode(dominantMode);

        // For XL items, check if charges should be applied
        if (isXL && !this.config.addChargesForHDItems) {
            chargeAmount = 0;
        }

        // For standard items, check free delivery threshold
        if (isStandard) {
            if (subTotalAmount >= this.config.freeDeliveryMilestone) {
                isFreeDelivery = true;
                chargeAmount = 0;
            } else {
                amountForFreeDelivery = parseFloat((this.config.freeDeliveryMilestone - subTotalAmount).toFixed(2));
            }
        }

        // Determine delivery message
        let deliveryMessage: string | null = null;
        if (cartAnalysis.isMixedDelivery) {
            deliveryMessage = 'Mixed delivery methods.';
        } else if (isXL) {
            deliveryMessage = 'XL item delivery.';
        } else if (isLarge) {
            deliveryMessage = 'Large item delivery.';
        }

        return {
            chargeAmount,
            deliveryMessage,
            isFreeDelivery,
            dominantMode,
            isMixedDelivery: cartAnalysis.isMixedDelivery,
            amountForFreeDelivery
        };
    }

    /**
     * Get free delivery milestone
     */
    public getFreeDeliveryMilestone(): number {
        return this.config.freeDeliveryMilestone;
    }

    /**
     * Get spend more message for free delivery
     */
    public getSpendMoreMessage(amountNeeded: number): string {
        return `Spend £${amountNeeded.toFixed(2)} more for free delivery. Eligible items only.`;
    }

    /**
     * Get free delivery qualified message
     */
    public getFreeDeliveryMessage(): string {
        return 'You have qualified for free delivery.';
    }

    /**
     * Get configuration for external use
     */
    public getConfig(): IDeliveryChargesConfig {
        return this.config;
    }
}

/**
 * Factory function to create DeliveryChargesUtil instance
 */
export const createDeliveryChargesUtil = (actionContext: IActionContext): DeliveryChargesUtil => {
    return new DeliveryChargesUtil(actionContext);
};
